// Утилитарная функция для получения переменных документа
// Используется во всех компонентах для подстановки переменных в шаблоны

import { createAllCases, createExecutiveRoleCases, createOrganizationCasesPreserveQuotes, createSimpleTextCases } from './declension';
import { detectSubjectGender } from './genderDetection';
import {
  DATE_FORMAT_OPTIONS,
  DATETIME_FORMAT_OPTIONS
} from '../config/templateVariables';
import { parseLocalDateTime, formatLocalDate, formatLocalDateTime } from './dateTime';
import { getDebtorDisplayName, getOrganizerDisplayName } from './entityDisplayName';

/**
 * Генерирует переменные склонения для базовой переменной
 * @param {string} baseName - базовое имя переменной (например, 'manager_name')
 * @param {string} fullName - полное ФИО для склонения
 * @returns {object} - объект с переменными склонения
 */
function generateDeclensionVariables(baseName, fullName) {
  const cases = createAllCases(fullName);
  
  return {
    [baseName]: cases.i, // оригинальная переменная остается как есть
    [`${baseName}_r`]: cases.r, // родительный падеж
    [`${baseName}_d`]: cases.d, // дательный падеж  
    [`${baseName}_v`]: cases.v, // винительный падеж
    [`${baseName}_t`]: cases.t, // творительный падеж
    [`${baseName}_p`]: cases.p, // предложный падеж
  };
}

function mapCasesToVariables(baseName, cases) {
  return {
    [baseName]: cases.i,
    [`${baseName}_r`]: cases.r,
    [`${baseName}_d`]: cases.d,
    [`${baseName}_v`]: cases.v,
    [`${baseName}_t`]: cases.t,
    [`${baseName}_p`]: cases.p,
  };
}

function createIdentityCases(value) {
  const base = String(value || '').trim();
  return { i: base, r: base, d: base, v: base, t: base, p: base };
}

function createEntrepreneurDebtorCases(rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return createIdentityCases('');

  const fioPart = value
    .replace(/^\s*(?:ип|индивидуальный\s+предприниматель)\s*/iu, '')
    .trim();

  const fioCases = fioPart ? createAllCases(fioPart) : createIdentityCases('');
  const prefix = 'ИП';
  const withPrefix = (text) => (text ? `${prefix} ${text}` : prefix);

  return {
    i: withPrefix(fioCases.i),
    r: withPrefix(fioCases.r),
    d: withPrefix(fioCases.d),
    v: withPrefix(fioCases.v),
    t: withPrefix(fioCases.t),
    p: withPrefix(fioCases.p),
  };
}

function generateDebtorNameDeclensionVariables(baseName, debtor, debtorDisplayName) {
  const value = String(debtorDisplayName || '').trim();
  const type = String(debtor?.type || '').trim();

  if (!value) {
    return mapCasesToVariables(baseName, createIdentityCases(''));
  }

  // Наименование юрлица не склоняем.
  if (type === 'legal') {
    return mapCasesToVariables(baseName, createIdentityCases(value));
  }

  // Для ИП сохраняем ОПФ в виде "ИП", склоняем только ФИО.
  if (type === 'entrepreneur' || /^\s*(?:ип|индивидуальный\s+предприниматель)\b/iu.test(value)) {
    return mapCasesToVariables(baseName, createEntrepreneurDebtorCases(value));
  }

  return mapCasesToVariables(baseName, createAllCases(value));
}

function generateOrganizationDeclensionVariables(baseName, value) {
  const cases = createOrganizationCasesPreserveQuotes(value);
  return {
    [baseName]: cases.i,
    [`${baseName}_r`]: cases.r,
    [`${baseName}_d`]: cases.d,
    [`${baseName}_v`]: cases.v,
    [`${baseName}_t`]: cases.t,
    [`${baseName}_p`]: cases.p,
  };
}

function generateSimpleTextDeclensionVariables(baseName, value) {
  const cases = createSimpleTextCases(value);
  return {
    [baseName]: cases.i,
    [`${baseName}_r`]: cases.r,
    [`${baseName}_d`]: cases.d,
    [`${baseName}_v`]: cases.v,
    [`${baseName}_t`]: cases.t,
    [`${baseName}_p`]: cases.p,
  };
}

function generateExecutiveRoleVariables(baseName, titleOrCases) {
  const cases = typeof titleOrCases === 'string'
    ? createExecutiveRoleCases(titleOrCases)
    : (titleOrCases || createExecutiveRoleCases(''));
  return {
    [baseName]: cases.i,
    [`${baseName}_r`]: cases.r,
    [`${baseName}_d`]: cases.d,
    [`${baseName}_v`]: cases.v,
    [`${baseName}_t`]: cases.t,
    [`${baseName}_p`]: cases.p,
  };
}

function generateCourtDeclensionVariables(baseName, fullName) {
  const value = (fullName || '').trim();
  if (!value) {
    return {
      [baseName]: '',
      [`${baseName}_r`]: '',
      [`${baseName}_d`]: '',
      [`${baseName}_v`]: '',
      [`${baseName}_t`]: '',
      [`${baseName}_p`]: '',
    };
  }

  const appealMatch = value.match(/^(.*)\s+апелляционный\s+суд(?:\s+|$)(.*)$/iu);
  const match = value.match(/^(Арбитражный(?:\s+[А-Яа-яё-]+)*)\s+суд(?:\s+|$)(.*)$/iu);
  let forms;

  if (appealMatch) {
    const prefixRaw = (appealMatch[1] || '').trim();
    const tailRaw = (appealMatch[2] || '').trim();
    const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);
    const ordinalMap = {
      'первый': { r: 'первого', d: 'первому', v: 'первый', t: 'первым', p: 'первом' },
      'второй': { r: 'второго', d: 'второму', v: 'второй', t: 'вторым', p: 'втором' },
      'третий': { r: 'третьего', d: 'третьему', v: 'третий', t: 'третьим', p: 'третьем' },
      'четвертый': { r: 'четвертого', d: 'четвертому', v: 'четвертый', t: 'четвертым', p: 'четвертом' },
      'пятый': { r: 'пятого', d: 'пятому', v: 'пятый', t: 'пятым', p: 'пятом' },
      'шестой': { r: 'шестого', d: 'шестому', v: 'шестой', t: 'шестым', p: 'шестом' },
      'седьмой': { r: 'седьмого', d: 'седьмому', v: 'седьмой', t: 'седьмым', p: 'седьмом' },
      'восьмой': { r: 'восьмого', d: 'восьмому', v: 'восьмой', t: 'восьмым', p: 'восьмом' },
      'девятый': { r: 'девятого', d: 'девятому', v: 'девятый', t: 'девятым', p: 'девятом' },
      'десятый': { r: 'десятого', d: 'десятому', v: 'десятый', t: 'десятым', p: 'десятом' },
      'одиннадцатый': { r: 'одиннадцатого', d: 'одиннадцатому', v: 'одиннадцатый', t: 'одиннадцатым', p: 'одиннадцатом' },
      'двенадцатый': { r: 'двенадцатого', d: 'двенадцатому', v: 'двенадцатый', t: 'двенадцатым', p: 'двенадцатом' },
      'тринадцатый': { r: 'тринадцатого', d: 'тринадцатому', v: 'тринадцатый', t: 'тринадцатым', p: 'тринадцатом' },
      'четырнадцатый': { r: 'четырнадцатого', d: 'четырнадцатому', v: 'четырнадцатый', t: 'четырнадцатым', p: 'четырнадцатом' },
      'пятнадцатый': { r: 'пятнадцатого', d: 'пятнадцатому', v: 'пятнадцатый', t: 'пятнадцатым', p: 'пятнадцатом' },
      'шестнадцатый': { r: 'шестнадцатого', d: 'шестнадцатому', v: 'шестнадцатый', t: 'шестнадцатым', p: 'шестнадцатом' },
      'семнадцатый': { r: 'семнадцатого', d: 'семнадцатому', v: 'семнадцатый', t: 'семнадцатым', p: 'семнадцатом' },
      'восемнадцатый': { r: 'восемнадцатого', d: 'восемнадцатому', v: 'восемнадцатый', t: 'восемнадцатым', p: 'восемнадцатом' },
      'девятнадцатый': { r: 'девятнадцатого', d: 'девятнадцатому', v: 'девятнадцатый', t: 'девятнадцатым', p: 'девятнадцатом' },
      'двадцатый': { r: 'двадцатого', d: 'двадцатому', v: 'двадцатый', t: 'двадцатым', p: 'двадцатом' },
      'двадцать первый': { r: 'двадцать первого', d: 'двадцать первому', v: 'двадцать первый', t: 'двадцать первым', p: 'двадцать первом' }
    };
    const restTokens = prefixRaw.split(/\s+/);
    let prefixCases = null;
    const adjustArbitrationAdj = (value, caseKey) => {
      if (!value) return value;
      const adjCases = {
        i: 'арбитражный',
        r: 'арбитражного',
        d: 'арбитражному',
        v: 'арбитражный',
        t: 'арбитражным',
        p: 'арбитражном'
      };
      const replacement = adjCases[caseKey] || adjCases.i;
      return value.replace(/(^|\s)арбитражный(?=\s|$)/giu, `$1${replacement}`);
    };
    if (restTokens.length >= 1) {
      const firstToken = restTokens[0].toLowerCase();
      const rest = restTokens.slice(1).join(' ').trim();
      const ordinal = ordinalMap[firstToken];
      if (ordinal) {
        const restCases = rest ? createAllCases(rest) : { i: '', r: '', d: '', v: '', t: '', p: '' };
        prefixCases = {
          i: `${capitalizeFirst(firstToken)}${rest ? ` ${adjustArbitrationAdj(restCases.i, 'i')}` : ''}`.trim(),
          r: `${capitalizeFirst(ordinal.r)}${rest ? ` ${adjustArbitrationAdj(restCases.r, 'r')}` : ''}`.trim(),
          d: `${capitalizeFirst(ordinal.d)}${rest ? ` ${adjustArbitrationAdj(restCases.d, 'd')}` : ''}`.trim(),
          v: `${capitalizeFirst(ordinal.v)}${rest ? ` ${adjustArbitrationAdj(restCases.v, 'v')}` : ''}`.trim(),
          t: `${capitalizeFirst(ordinal.t)}${rest ? ` ${adjustArbitrationAdj(restCases.t, 't')}` : ''}`.trim(),
          p: `${capitalizeFirst(ordinal.p)}${rest ? ` ${adjustArbitrationAdj(restCases.p, 'p')}` : ''}`.trim()
        };
      }
    }
    if (!prefixCases) {
      prefixCases = prefixRaw ? createAllCases(prefixRaw) : {
        i: '',
        r: '',
        d: '',
        v: '',
        t: '',
        p: ''
      };
    }
    const adjCases = {
      i: 'апелляционный',
      r: 'апелляционного',
      d: 'апелляционному',
      v: 'апелляционный',
      t: 'апелляционным',
      p: 'апелляционном'
    };
    const suffix = tailRaw ? ` ${tailRaw}` : '';
    const space = prefixRaw ? ' ' : '';
    forms = {
      i: `${prefixCases.i}${space}${adjCases.i} суд${suffix}`.replace(/\s+/g, ' ').trim(),
      r: `${prefixCases.r}${space}${adjCases.r} суда${suffix}`.replace(/\s+/g, ' ').trim(),
      d: `${prefixCases.d}${space}${adjCases.d} суду${suffix}`.replace(/\s+/g, ' ').trim(),
      v: `${prefixCases.v}${space}${adjCases.v} суд${suffix}`.replace(/\s+/g, ' ').trim(),
      t: `${prefixCases.t}${space}${adjCases.t} судом${suffix}`.replace(/\s+/g, ' ').trim(),
      p: `${prefixCases.p}${space}${adjCases.p} суде${suffix}`.replace(/\s+/g, ' ').trim()
    };
    const arbCases = {
      r: 'арбитражного',
      d: 'арбитражному',
      v: 'арбитражный',
      t: 'арбитражным',
      p: 'арбитражном'
    };
    const replaceArb = (value, replacement) =>
      value.replace(/(^|\s)арбитражный(?=\s|$)/giu, `$1${replacement}`);
    forms = {
      ...forms,
      r: replaceArb(forms.r, arbCases.r),
      d: replaceArb(forms.d, arbCases.d),
      v: replaceArb(forms.v, arbCases.v),
      t: replaceArb(forms.t, arbCases.t),
      p: replaceArb(forms.p, arbCases.p)
    };
  } else if (match) {
    const prefixRaw = match[1] || '';
    const tailRaw = match[2] || '';
    const prefixCases = createAllCases(prefixRaw.trim());
    const prefixOverrides = {
      i: prefixCases.i.replace(/^Арбитражный/i, 'Арбитражный'),
      r: prefixCases.r.replace(/^Арбитражный/i, 'Арбитражного'),
      d: prefixCases.d.replace(/^Арбитражный/i, 'Арбитражному'),
      v: prefixCases.v.replace(/^Арбитражный/i, 'Арбитражный'),
      t: prefixCases.t.replace(/^Арбитражный/i, 'Арбитражным'),
      p: prefixCases.p.replace(/^Арбитражный/i, 'Арбитражном')
    };
    const tail = tailRaw.replace(/^\s+/, '');
    const suffix = tail ? ` ${tail}` : '';
    forms = {
      i: `${prefixOverrides.i} суд${suffix}`.replace(/\s+/g, ' ').trim(),
      r: `${prefixOverrides.r} суда${suffix}`.replace(/\s+/g, ' ').trim(),
      d: `${prefixOverrides.d} суду${suffix}`.replace(/\s+/g, ' ').trim(),
      v: `${prefixOverrides.v} суд${suffix}`.replace(/\s+/g, ' ').trim(),
      t: `${prefixOverrides.t} судом${suffix}`.replace(/\s+/g, ' ').trim(),
      p: `${prefixOverrides.p} суде${suffix}`.replace(/\s+/g, ' ').trim()
    };
  } else {
    const cases = createAllCases(value);
    forms = {
      i: cases.i,
      r: cases.r,
      d: cases.d,
      v: cases.v,
      t: cases.t,
      p: cases.p
    };
  }

  return {
    [baseName]: forms.i,
    [`${baseName}_r`]: forms.r,
    [`${baseName}_d`]: forms.d,
    [`${baseName}_v`]: forms.v,
    [`${baseName}_t`]: forms.t,
    [`${baseName}_p`]: forms.p,
  };
}

function buildCourtFullName(rawValue) {
  const raw = String(rawValue || '').trim();
  if (!raw) return '';
  const lowered = raw.toLowerCase();
  if (/апелляц/i.test(lowered) && /суд/i.test(lowered)) {
    return raw;
  }
  if (/арбитражный\s+суд/i.test(lowered)) {
    return raw;
  }
  if (/арбитражн/i.test(lowered) && !/суд/i.test(lowered)) {
    return `${raw} апелляционный суд`.replace(/\s+/g, ' ').trim();
  }
  return `Арбитражный суд ${raw}`.replace(/\s+/g, ' ').trim();
}

const formatCurrency = (value, fallback = 'Не указано') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const number = Number(value);
  if (Number.isFinite(number)) {
    return `${number.toLocaleString('ru-RU')} ₽`;
  }
  return String(value);
};

const formatPercent = (value, fallback = 'Не указан') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const number = Number(value);
  if (Number.isFinite(number)) {
    return `${number.toLocaleString('ru-RU')}%`;
  }
  return `${value}%`;
};

const formatBankAccount = (account, fallback = 'Счет не указан', options = {}) => {
  let resolvedFallback = fallback;
  let resolvedOptions = options;
  if (fallback && typeof fallback === 'object' && !Array.isArray(fallback)) {
    resolvedOptions = fallback;
    resolvedFallback = 'Счет не указан';
  }
  const { includePurpose = true } = resolvedOptions || {};
  if (!account) return resolvedFallback;
  const purposeLabels = {
    main: 'основной',
    special: 'специальный',
    deposit: 'для задатка',
    payment: 'для оплаты',
  };
  const parts = [
    account.account_number ? `р/с ${account.account_number}` : null,
    account.bank_name,
    account.corr_account ? `к/с ${account.corr_account}` : null,
    account.bik ? `БИК ${account.bik}` : null,
  ].filter(Boolean);
  if (includePurpose && account.purpose) {
    const purposeText = purposeLabels[account.purpose] || account.purpose;
    parts.push(`назначение: ${purposeText}`);
  }
  return parts.length > 0 ? parts.join(', ') : resolvedFallback;
};

const resolveAccountOwnerName = (account, debtors = [], organizers = [], etps = []) => {
  if (!account) return '';
  const ownerType = String(account.owner_type || account.ownerType || '').trim().toLowerCase();
  const ownerId = account.owner_id ?? account.ownerId ?? null;
  const byId = (list) => list.find((item) => String(item?.id ?? '') === String(ownerId ?? ''));

  if (ownerType === 'debtor') {
    const debtor = byId(debtors);
    return getDebtorDisplayName(debtor) || debtor?.name || account.owner_name || '';
  }
  if (ownerType === 'organizer') {
    const organizer = byId(organizers);
    return getOrganizerDisplayName(organizer) || organizer?.name || account.owner_name || '';
  }
  if (ownerType === 'platform') {
    const etp = byId(etps);
    return (etp?.operator || etp?.name || account.owner_name || '');
  }

  return account.owner_name || '';
};

const resolveAccountOwnerInn = (account, debtors = [], organizers = [], etps = []) => {
  if (!account) return '';
  const ownerType = String(account.owner_type || account.ownerType || '').trim().toLowerCase();
  const ownerId = account.owner_id ?? account.ownerId ?? null;
  const byId = (list) => list.find((item) => String(item?.id ?? '') === String(ownerId ?? ''));

  if (ownerType === 'debtor') return byId(debtors)?.inn || '';
  if (ownerType === 'organizer') return byId(organizers)?.inn || '';
  if (ownerType === 'platform') return byId(etps)?.inn || '';

  return '';
};

const resolveAccountOwnerTypeCode = (account) => {
  if (!account) return '';
  return String(account.owner_type || account.ownerType || '').trim().toLowerCase();
};

const resolveAccountOwnerTypeLabel = (account) => {
  const code = resolveAccountOwnerTypeCode(account);
  const labels = {
    debtor: 'Должник',
    organizer: 'Организатор',
    platform: 'Площадка',
  };
  return labels[code] || '';
};

const toSurnameWithInitials = (fullName) => {
  const raw = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!raw) return '';
  const parts = raw.split(' ').filter(Boolean);
  if (!parts.length) return '';
  const surname = parts[0] || '';
  const initials = parts
    .slice(1, 3)
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join('');
  return `${surname}${initials ? ` ${initials}` : ''}`.trim();
};

const generateShortNameDeclensionVariables = (baseName, shortName) => {
  const raw = String(shortName || '').trim().replace(/\s+/g, ' ');
  if (!raw) {
    return {
      [baseName]: '',
      [`${baseName}_r`]: '',
      [`${baseName}_d`]: '',
      [`${baseName}_v`]: '',
      [`${baseName}_t`]: '',
      [`${baseName}_p`]: '',
    };
  }

  const [surname = '', ...tail] = raw.split(' ');
  const tailText = tail.join(' ').trim();
  const surnameCases = createAllCases(surname || raw);
  const withTail = (value) => `${value || ''}${tailText ? ` ${tailText}` : ''}`.trim();

  return {
    [baseName]: raw,
    [`${baseName}_r`]: withTail(surnameCases.r),
    [`${baseName}_d`]: withTail(surnameCases.d),
    [`${baseName}_v`]: withTail(surnameCases.v),
    [`${baseName}_t`]: withTail(surnameCases.t),
    [`${baseName}_p`]: withTail(surnameCases.p),
  };
};
export function formatDate(date) {
  return formatLocalDate(date);
}

export function formatDateTime(date) {
  return formatLocalDateTime(date);
}

const MONTH_NAMES = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
];

const extractIsoParts = (value) => {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  const match = raw.match(
    /^(\\d{4})-(\\d{2})-(\\d{2})(?:[T\\s](\\d{2}):(\\d{2})(?::(\\d{2}))?)?(?:\\.\\d+)?(?:Z|[+-]\\d{2}:?\\d{2})?$/
  );
  if (!match) return null;
  return {
    y: match[1],
    m: match[2],
    d: match[3],
    hh: match[4],
    mm: match[5]
  };
};

const pad2 = (value) => String(value).padStart(2, '0');

const formatLongDateParts = (parts) => {
  const day = String(Number(parts.d));
  const monthIndex = Math.max(0, Number(parts.m) - 1);
  const monthName = MONTH_NAMES[monthIndex] || '';
  return `${day} ${monthName} ${parts.y}`.trim();
};

const formatTimeWords = (hh, min) => {
  const hours = String(Number(hh || 0));
  const minutes = pad2(min || 0);
  return `${hours} часов ${minutes} минут`.trim();
};

const formatTimeShort = (hh, min) => {
  const hours = String(Number(hh || 0));
  const minutes = pad2(min || 0);
  return `${hours} ч. ${minutes} мин.`.trim();
};

const parseDateValue = (value) => {
  return parseLocalDateTime(value);
};

const formatDateByOption = (value, formatId = 'default') => {
  const parts = extractIsoParts(value);
  if (parts) {
    const dd = pad2(parts.d);
    const mm = pad2(parts.m);
    switch (formatId) {
      case 'long':
        return formatLongDateParts(parts);
      case 'iso':
        return `${parts.y}-${mm}-${dd}`;
      default:
        return `${dd}.${mm}.${parts.y}`;
    }
  }

  const date = parseDateValue(value);
  if (!date) return 'Дата не указана';

  switch (formatId) {
    case 'long':
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    case 'iso':
      return date.toISOString().slice(0, 10);
    default:
      return formatDate(date);
  }
};

const formatDateTimeByOption = (value, formatId = 'default') => {
  const parts = extractIsoParts(value);
  if (parts) {
    const dd = pad2(parts.d);
    const mm = pad2(parts.m);
    const hh = pad2(parts.hh || '00');
    const min = pad2(parts.mm || '00');
    switch (formatId) {
      case 'long':
        return `${formatLongDateParts(parts)} ${hh}:${min}`.trim();
      case 'date':
        return `${dd}.${mm}.${parts.y}`;
      case 'date_long':
        return formatLongDateParts(parts);
      case 'time':
        return `${hh}:${min}`;
      case 'time_words':
        return formatTimeWords(hh, min);
      case 'time_short':
        return formatTimeShort(hh, min);
      default:
        return `${dd}.${mm}.${parts.y} ${hh}:${min}`;
    }
  }

  const date = parseDateValue(value);
  if (!date) return 'Дата и время не указаны';

  switch (formatId) {
    case 'long':
      return date.toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    case 'date':
      return date.toLocaleDateString('ru-RU');
    case 'time':
      return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
    case 'time_words':
      return formatTimeWords(date.getHours(), date.getMinutes());
    case 'time_short':
      return formatTimeShort(date.getHours(), date.getMinutes());
    case 'date_long':
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    default:
      return formatDateTime(date);
  }
};

export const getDocumentVariablesData = (auction, debtors = [], organizers = [], lots = [], etps = []) => {
  
  const debtor = debtors?.find(d => d.id === auction?.debtor);
  const organizer = organizers?.find(o => o.id === auction?.organizer);
  const debtorFullName = getDebtorDisplayName(debtor) || debtor?.name || '';
  const organizerFullName = getOrganizerDisplayName(organizer) || organizer?.name || '';
  const etp = etps?.find(e => e.id === auction?.etp) || null;
  const debtorManager = debtor?.arbitration_manager_data
    || organizers?.find(o => o.id === debtor?.arbitration_manager)
    || null;
  const inspectionPhone = auction?.inspection_phone || organizer?.phone || '';
  const inspectionEmail = auction?.inspection_email || organizer?.email || '';
  const executiveRoleCases = createExecutiveRoleCases(organizer?.executive_role || 'Должность не указана');
  const debtorTypeCode = debtor?.type || null;
  const debtorGender = (() => {
    const detected = detectSubjectGender(debtorFullName || '', { explicitType: debtorTypeCode || '' });
    if (detected === 'male' || detected === 'female' || detected === 'neuter') return detected;
    return 'unknown';
  })();
  const debtorManagerGender = (() => {
    const detected = detectSubjectGender(debtorManager?.name || '', { explicitType: null });
    if (detected === 'male' || detected === 'female' || detected === 'neuter') return detected;
    return 'unknown';
  })();
  const organizerTypeCode = organizer?.type || null;
  const rawLots = Array.isArray(lots) ? lots : [];
  const hasAuctionField = rawLots.some((lot) => lot && (lot.auction !== undefined || lot.auction_id !== undefined || lot.auctionId !== undefined));
  const auctionId = auction?.id != null ? String(auction.id) : '';
  const scopedLots = (auctionId && hasAuctionField)
    ? rawLots.filter((lot) => String(lot?.auction || lot?.auction_id || lot?.auctionId || '') === auctionId)
    : rawLots;
  const lotsCount = scopedLots.length;
  const lotsCountCategory = lotsCount <= 0 ? 'none' : (lotsCount === 1 ? 'single' : 'multiple');
  const lotsCountBucket = lotsCount <= 0 ? 'none' : (lotsCount === 1 ? 'single' : (lotsCount === 2 ? 'two' : 'many'));
  const tradeFormCode = auction?.auction_form || 'open';
  const tradeFormLabel = tradeFormCode === 'open' ? 'Открытая' : 'Закрытая';
  const priceOfferFormCode = auction?.price_offer_form || '';
  const auctionFormCode = auction?.auction_type || '';
  const conductorCode = organizerTypeCode === 'specialized_org'
    ? 'ot'
    : (organizerTypeCode === 'arbitrage_manager' ? 'au' : null);
  const managerActBasisCode = debtor?.manager_act_independent === true
    ? 'separate'
    : (debtor?.manager_act_independent === false ? 'same' : 'unknown');
  
  const normalizedLots = [...scopedLots].sort((a, b) => {
    const aNumber = Number.parseInt(String(a?.lot_number ?? ''), 10);
    const bNumber = Number.parseInt(String(b?.lot_number ?? ''), 10);
    const aValid = Number.isFinite(aNumber) && aNumber > 0;
    const bValid = Number.isFinite(bNumber) && bNumber > 0;
    if (aValid && bValid && aNumber !== bNumber) return aNumber - bNumber;
    if (aValid && !bValid) return -1;
    if (!aValid && bValid) return 1;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });

  const numberedLots = normalizedLots.map((lot, index) => ({
    ...lot,
    lot_number: Number.isFinite(Number.parseInt(String(lot?.lot_number ?? ''), 10))
      ? Number.parseInt(String(lot?.lot_number ?? ''), 10)
      : (index + 1)
  }));
  const primaryLotNumber = lotsCount === 1 ? (numberedLots[0]?.lot_number || 1) : '';

  const formatLotVat = (lot) => {
    const vatText = (lot?.vat_text || '').trim();
    return vatText ? ` (${vatText})` : '';
  };

  const formatLotDescriptionLine = (lot, isLast) => {
    const rawDesc = (lot?.description || lot?.name || '').trim();
    const desc = rawDesc.replace(/[.;:\s]+$/g, '').trim();
    const priceNumber = Number(lot?.start_price);
    const priceText = Number.isFinite(priceNumber)
      ? priceNumber.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      : String(lot?.start_price || '').trim();
    const vatText = (lot?.vat_text || '').trim();
    let line = `Лот №${lot.lot_number}: ${desc}. Начальная цена продажи Лота ${lot.lot_number}: ${priceText} рублей`;
    if (vatText) {
      line += `. (${vatText})`;
    } else {
      line += '.';
    }
    if (isLast) {
      if (!line.trim().endsWith('.')) line += '.';
    } else {
      line = line.replace(/\.+$/g, '').trimEnd() + ';';
    }
    return line;
  };

  const propertyLotsList = numberedLots.map((lot) =>
    `Лот №${lot.lot_number}: ${lot.name} - ${lot.start_price?.toLocaleString('ru-RU')} ₽${formatLotVat(lot)}`
  );
  const propertyLots = propertyLotsList.join('\n') || 'Лоты не определены';
  const propertyLotsDescLines = numberedLots.length > 0
    ? numberedLots.map((lot, idx) => formatLotDescriptionLine(lot, idx === numberedLots.length - 1)).join('<br/>')
    : 'Лоты не определены';

  const extractCreditorNames = (lot) => {
    const details = Array.isArray(lot?.pledge_creditors_details)
      ? lot.pledge_creditors_details
      : (Array.isArray(lot?.pledge_creditors) ? lot.pledge_creditors : []);
    const names = details
      .map((item) => (typeof item === 'string' ? item : item?.name))
      .map((name) => (name || '').trim())
      .filter(Boolean);
    if (names.length === 0) {
      return ['Кредитор не указан'];
    }
    return Array.from(new Set(names));
  };

  const pledgeLots = numberedLots.filter((lot) => !!lot?.pledge_creditor_satisfaction);
  const pledgeLotsCount = pledgeLots.length;
  const pledgeLotsCountCategory = pledgeLotsCount <= 0 ? 'none' : (pledgeLotsCount === 1 ? 'single' : 'multiple');
  const pledgeLotsCountBucket = pledgeLotsCount <= 0 ? 'none' : (pledgeLotsCount === 1 ? 'single' : (pledgeLotsCount === 2 ? 'two' : 'many'));

  const pledgeLotsWithCreditors = pledgeLots.map((lot) => ({
    ...lot,
    creditor_names: extractCreditorNames(lot)
  }));
  const pledgeSingleCreditorLots = pledgeLotsWithCreditors.filter((lot) => lot.creditor_names.length === 1);
  const pledgeMultiCreditorLots = pledgeLotsWithCreditors.filter((lot) => lot.creditor_names.length > 1);

  const pledgeMultiCreditorCategory = pledgeMultiCreditorLots.length <= 0
    ? 'none'
    : (pledgeMultiCreditorLots.length === 1 ? 'single' : 'multiple');

  const singleCreditorGroupsMap = new Map();
  pledgeSingleCreditorLots.forEach((lot) => {
    const creditor = lot.creditor_names[0] || 'Кредитор не указан';
    if (!singleCreditorGroupsMap.has(creditor)) {
      singleCreditorGroupsMap.set(creditor, []);
    }
    singleCreditorGroupsMap.get(creditor).push(lot);
  });

  const singleCreditorGroups = Array.from(singleCreditorGroupsMap.entries()).map(([creditor, groupLots]) => ({
    creditor,
    lots: groupLots
  }));
  const singleCreditorUniqueCount = singleCreditorGroups.length;
  const singleCreditorUniqueCategory = singleCreditorUniqueCount <= 0
    ? 'none'
    : (singleCreditorUniqueCount === 1 ? 'single' : 'multiple');
  const sameCreditorGroups = singleCreditorGroups.filter((group) => group.lots.length > 1);
  const sameCreditorGroupPresent = sameCreditorGroups.length > 0 ? 'yes' : 'no';

  const formatLotNumbers = (groupLots) => groupLots
    .slice()
    .sort((a, b) => a.lot_number - b.lot_number)
    .map((lot) => lot.lot_number)
    .join(', ');

  const pledgeSingleCreditorGroups = sameCreditorGroups.map((group) => ({
    creditor_name: group.creditor,
    lot_numbers: formatLotNumbers(group.lots),
    lot_count: group.lots.length,
  }));
  const pledgeSingleCreditorSoloLots = singleCreditorGroups
    .filter((group) => group.lots.length === 1)
    .map((group) => ({
      creditor_name: group.creditor,
      lot_number: group.lots[0].lot_number,
    }));
  const pledgeMultiCreditorEntries = pledgeMultiCreditorLots
    .flatMap((lot) => lot.creditor_names.map((creditor) => ({
      lot_number: lot.lot_number,
      creditor_name: creditor,
    })));

  const hasDebtLots = numberedLots.some((lot) => String(lot?.category || '') === 'debt');
  // Имуществом считаем любой лот, который не относится к дебиторской задолженности.
  const hasAssetLots = numberedLots.some((lot) => String(lot?.category || '') !== 'debt');
  const propertyDebtCategory = hasDebtLots ? 'yes' : 'no';
  const propertyAssetCategory = hasAssetLots ? 'yes' : 'no';
  const propertyMixedCategory = hasDebtLots && hasAssetLots ? 'yes' : 'no';

  const buildAuctionTypeOfferFormText = () => {
    const offerAdjInstrFem = (auction?.price_offer_form || 'open') === 'open' ? 'открытой' : 'закрытой';

    if (auction?.auction_type === 'public_offer') {
      return 'посредством публичного предложения';
    }

    const kind = auction?.auction_type === 'contest' ? 'конкурса' : 'аукциона';
    return `в форме ${kind} с ${offerAdjInstrFem} формой подачи предложения о цене`;
  };

  const totalLotsPrice = scopedLots.reduce((sum, lot) => sum + (Number(lot.start_price) || 0), 0) || 0;
  const depositPercentValue = auction?.deposit_amount;
  const depositPercentText = depositPercentValue !== null && depositPercentValue !== undefined && depositPercentValue !== ''
    ? Number(depositPercentValue).toLocaleString('ru-RU')
    : null;
  const depositAccountDetails = formatBankAccount(auction?.deposit_account_details, { includePurpose: false });
  const paymentAccountDetails = formatBankAccount(auction?.payment_account_details, { includePurpose: false });
  const depositAccountOwnerName = resolveAccountOwnerName(auction?.deposit_account_details, debtors, organizers, etps);
  const paymentAccountOwnerName = resolveAccountOwnerName(auction?.payment_account_details, debtors, organizers, etps);
  const depositAccountOwnerInn = resolveAccountOwnerInn(auction?.deposit_account_details, debtors, organizers, etps);
  const paymentAccountOwnerInn = resolveAccountOwnerInn(auction?.payment_account_details, debtors, organizers, etps);
  const depositAccountOwnerTypeCode = resolveAccountOwnerTypeCode(auction?.deposit_account_details);
  const paymentAccountOwnerTypeCode = resolveAccountOwnerTypeCode(auction?.payment_account_details);
  const depositAccountOwnerTypeLabel = resolveAccountOwnerTypeLabel(auction?.deposit_account_details);
  const paymentAccountOwnerTypeLabel = resolveAccountOwnerTypeLabel(auction?.payment_account_details);
  const debtorManagerNameShort = toSurnameWithInitials(debtorManager?.name || '');
  const DEBTOR_TYPE_LABELS = {
    legal: 'Юридическое лицо',
    entrepreneur: 'Индивидуальный предприниматель',
    individual: 'Физическое лицо'
  };
  const ORGANIZER_TYPE_LABELS = {
    arbitrage_manager: 'Арбитражный управляющий',
    specialized_org: 'Специализированная организация'
  };
  const ORGANIZER_ENTITY_TYPE_LABELS = {
    legal: 'Юридическое лицо',
    entrepreneur: 'Индивидуальный предприниматель',
    individual: 'Физическое лицо'
  };
  const debtorTypeLabel = debtor ? (DEBTOR_TYPE_LABELS[debtor.type] || 'Тип не указан') : 'Тип не указан';
  const organizerTypeLabel = organizer ? (ORGANIZER_TYPE_LABELS[organizer.type] || 'Тип не указан') : 'Тип не указан';
  const organizerEntityTypeCode = organizer
    ? (organizer.entity_type || (organizer.type === 'arbitrage_manager' ? 'individual' : 'legal'))
    : 'unknown';
  const organizerEntityTypeLabel = organizer
    ? (ORGANIZER_ENTITY_TYPE_LABELS[organizerEntityTypeCode] || 'Тип не указан')
    : 'Тип не указан';
  const caseActTypeLabels = {
    decision: 'Решение',
    determination: 'Определение',
    decree: 'Постановление'
  };
  const caseActTypeLabel = debtor ? (caseActTypeLabels[debtor.case_act_type] || 'Решение') : 'Решение';
  const managerActTypeLabel = debtor
    ? (caseActTypeLabels[debtor.manager_act_type] || caseActTypeLabel || 'Решение')
    : 'Решение';
  const managerActDateValue = debtor
    ? (debtor.manager_act_independent
        ? (debtor.manager_act_date || null)
        : (debtor.manager_act_date || debtor.case_act_date || null))
    : null;
  const debtorProcedureCode = debtor?.procedure || null;
  const debtorProcedureLabels = {
    observation: 'наблюдения',
    competition: 'конкурсного производства',
    financial_rehabilitation: 'финансового оздоровления',
    external_management: 'внешнего управления',
    citizen_debt_restructuring: 'реструктуризации долгов гражданина',
    citizen_property_realization: 'реализации имущества гражданина',
  };
  const defaultProcedureByType = (debtor?.type === 'individual' || debtor?.type === 'entrepreneur')
    ? 'citizen_property_realization'
    : 'competition';
  const debtorProcedureLabel = debtor
    ? (debtorProcedureLabels[debtorProcedureCode] || debtorProcedureLabels[defaultProcedureByType] || '')
    : '';

  const managerRoleByProcedure = {
    observation: 'временный',
    competition: 'конкурсный',
    financial_rehabilitation: 'административный',
    external_management: 'внешний',
    citizen_debt_restructuring: 'финансовый',
    citizen_property_realization: 'финансовый',
  };
  const debtorManagerRoleCode = debtor?.manager_role || (managerRoleByProcedure[debtorProcedureCode] || managerRoleByProcedure[defaultProcedureByType] || null);
  // debtors_manager_role_code (человекочитаемый) больше не нужен — оставляем только код-прилагательное
  const arbitrationCourtFull = buildCourtFullName(debtor?.arbitration_court || '');
  const caseActCourtFull = buildCourtFullName(debtor?.case_act_court || debtor?.arbitration_court || '');
  const managerActCourtFull = buildCourtFullName(debtor?.manager_act_court || debtor?.case_act_court || debtor?.arbitration_court || '');
  const normalizeCourt = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const hasCaseActCourt = Boolean(normalizeCourt(caseActCourtFull));
  const hasManagerActCourt = Boolean(normalizeCourt(managerActCourtFull));
  const actsCourtRelationCode = (!hasCaseActCourt || !hasManagerActCourt)
    ? 'unknown'
    : (normalizeCourt(caseActCourtFull) === normalizeCourt(managerActCourtFull) ? 'same' : 'different');

  // Формируем полный список переменных согласно TEMPLATE_VARIABLES
  const variablesData = {
    // Основная информация
    contract_number: `№ AOT-${auction?.id || 'XXX'}`,
    city: organizer?.address?.split(',')[0]?.trim() || 'Москва',
    
    // Должник
    debtor_name: debtorFullName || 'Должник не указан',
    debtor_gender: debtorGender,
    debtor_kind: debtorTypeCode,
    debtor_type_code: debtorTypeCode,
    debtor_type: debtorTypeLabel,
    debtor_legal_form: debtor?.legal_form || 'Организационно-правовая форма не указана',
    debtor_inn: debtor?.inn || 'ИНН не указан',
    debtor_kpp: debtor?.kpp || 'КПП не указан',
    debtor_ogrn: debtor?.ogrn || 'ОГРН не указан',
    debtor_snils: debtor?.snils || 'СНИЛС не указан',
    debtor_address: debtor?.address || 'Адрес не указан',
    debtor_email: debtor?.email || '',
    debtor_phone: debtor?.phone || '',
    debtor_case_number: debtor?.case_number || 'Номер дела не указан',
    debtor_arbitration_court: arbitrationCourtFull || 'Арбитражный суд не указан',
    debtor_case_act_court: caseActCourtFull || 'Суд не указан',
    debtor_manager_act_court: managerActCourtFull || 'Суд не указан',
    debtor_acts_court_relation_code: actsCourtRelationCode,
    debtor_procedure_code: debtorProcedureCode || defaultProcedureByType,
    debtor_procedure: debtorProcedureLabel || 'Процедура не указана',
    // В переменной debtor_manager_role храним именно код (прилагательное), например: "конкурсный"
    debtor_manager_role: debtorManagerRoleCode || '',
    debtor_manager_gender: debtorManagerGender,
    debtor_case_act_type: caseActTypeLabel,
    debtor_manager_act_type: managerActTypeLabel,
    debtor_manager_name: debtorManager?.name || 'Управляющий не указан',
    debtor_manager_name_short: debtorManagerNameShort || 'Управляющий не указан',
    debtor_manager_inn: debtorManager?.inn || 'ИНН не указан',
    debtor_manager_snils: debtorManager?.snils || 'СНИЛС не указан',
    debtor_manager_sro_info: debtorManager?.sro_info || 'Сведения о СРО не указаны',
    debtor_manager_sro_name: debtorManager?.sro_name || '',
    debtor_manager_sro_inn: debtorManager?.sro_inn || '',
    debtor_manager_sro_ogrn: debtorManager?.sro_ogrn || '',
    debtor_manager_sro_address: debtorManager?.sro_address || '',
    debtor_manager_phone: debtorManager?.phone || '',
    debtor_manager_email: debtorManager?.email || '',
    debtor_manager_mailing_address: debtorManager?.mailing_address || 'Адрес для корреспонденции не указан',

    // Организатор
    organizer_name: organizerFullName || 'Организатор не указан',
    organizer_kind: organizerTypeCode,
    organizer_type_code: organizerTypeCode,
    organizer_type: organizerTypeLabel,
    organizer_entity_type_code: organizerEntityTypeCode,
    organizer_entity_type: organizerEntityTypeLabel,
    organizer_inn: organizer?.inn || 'ИНН не указан',
    organizer_ogrn: organizer?.ogrn || 'ОГРН/ОГРНИП не указан',
    organizer_kpp: organizer?.kpp || 'КПП не указан',
    organizer_address: organizer?.address || organizer?.mailing_address || 'Адрес не указан',
    organizer_mailing_address: organizer?.mailing_address || '',
    organizer_phone: organizer?.phone || '',
    organizer_email: organizer?.email || '',
    organizer_contacts: organizer?.contacts || '',
    organizer_details: organizer?.details || organizer?.bank_details || 'Реквизиты организатора',
    organizer_executive_role: executiveRoleCases.i || 'Должность не указана',
    organizer_executive_name: organizer?.executive_name || organizerFullName || 'Арбитражный управляющий',
    organizer_snils: organizer?.snils || 'СНИЛС не указан',
    organizer_signatory: organizer?.executive_name || organizerFullName || 'Арбитражный управляющий',
    
    // Другие участники
    buyer_name: 'Покупатель (будет определен по итогам торгов)',
    seller_name: organizerFullName || 'Организатор торгов',
    participant_name: 'Участник торгов (будет определен)',
    
    // Склонения ФИО (добавляем падежные формы с суффиксами)
    ...generateDebtorNameDeclensionVariables('debtor_name', debtor, debtorFullName || 'Должник не указан'),
    ...generateDeclensionVariables('debtor_manager_name', debtorManager?.name || 'Управляющий не указан'),
    ...generateShortNameDeclensionVariables('debtor_manager_name_short', debtorManagerNameShort || 'Управляющий не указан'),
    ...generateDeclensionVariables('organizer_executive_name', organizer?.executive_name || organizerFullName || 'Арбитражный управляющий'),
    ...generateExecutiveRoleVariables('organizer_executive_role', executiveRoleCases),
    ...generateDeclensionVariables('organizer_signatory', organizer?.executive_name || organizerFullName || 'Арбитражный управляющий'),
    ...generateDeclensionVariables('organizer_name', organizerFullName || 'Организатор не указан'),
    ...generateOrganizationDeclensionVariables('debtor_manager_sro_name', debtorManager?.sro_name || ''),
    ...generateSimpleTextDeclensionVariables('debtor_case_act_type', caseActTypeLabel || ''),
    ...generateSimpleTextDeclensionVariables('debtor_manager_act_type', managerActTypeLabel || ''),
    ...generateSimpleTextDeclensionVariables('deposit_account_owner_type_label', depositAccountOwnerTypeLabel || ''),
    ...generateSimpleTextDeclensionVariables('payment_account_owner_type_label', paymentAccountOwnerTypeLabel || ''),
    
    // Имущество и торги
    auction_lots_count: lotsCount,
    auction_lots_count_category: lotsCountCategory,
    auction_lots_count_bucket: lotsCountBucket,
    auction_conductor_code: conductorCode,
    auction_form_code: auctionFormCode,
    price_offer_form_code: priceOfferFormCode,
    pledge_lots_count: pledgeLotsCount,
    pledge_lots_count_category: pledgeLotsCountCategory,
    pledge_lots_count_bucket: pledgeLotsCountBucket,
    pledge_lots_same_creditor_group_present: sameCreditorGroupPresent,
    pledge_lots_multi_creditor_category: pledgeMultiCreditorCategory,
    pledge_lots_single_creditor_total_count: pledgeSingleCreditorLots.length,
    pledge_lots_single_creditor_unique_count: singleCreditorUniqueCount,
    pledge_lots_single_creditor_unique_category: singleCreditorUniqueCategory,
    pledge_single_creditor_groups: pledgeSingleCreditorGroups,
    pledge_single_creditor_solo_lots: pledgeSingleCreditorSoloLots,
    pledge_multi_creditor_entries: pledgeMultiCreditorEntries,
    property_has_debt_lots: propertyDebtCategory,
    property_has_asset_lots: propertyAssetCategory,
    property_has_debt_and_asset_lots: propertyMixedCategory,
    debtor_manager_act_basis_code: managerActBasisCode,
    property_description: propertyLots,
    property_lots: propertyLots,
    property_lots_desc_lines: propertyLotsDescLines,
    lot_number: primaryLotNumber,
    auction_type: auction?.price_offer_form === 'open' ? 'открытые' : 'закрытые',
    auction_trade_form: tradeFormLabel,
    auction_trade_form_text: buildAuctionTypeOfferFormText(),
    auction_form: auction?.auction_type === 'auction' ? 'аукцион' : 
                  auction?.auction_type === 'contest' ? 'конкурс' : 
                  auction?.auction_type === 'public_offer' ? 'публичное предложение' : 'торги',
    auction_location: organizer?.address || 'Место не указано',
    application_procedure: 'В соответствии с требованиями законодательства',
    auction_step: auction?.auction_step ? formatPercent(auction.auction_step) : 'Шаг не указан',
    contest_step: auction?.contest_step ? formatPercent(auction.contest_step) : 'Шаг не указан',
    price_decrease_rules: auction?.price_decrease_rules || 'Порядок снижения цены не указан',
    contest_conditions: auction?.contest_conditions || 'Условия конкурса не указаны',
    
    // Финансовые условия
    price: auction?.start_price ? formatCurrency(auction.start_price) : (totalLotsPrice > 0 ? formatCurrency(totalLotsPrice) : 'Цена не указана'),
    price_text: auction?.start_price ? formatCurrency(auction.start_price) : (totalLotsPrice > 0 ? formatCurrency(totalLotsPrice) : 'Цена не указана'),
    deposit_amount: formatPercent(depositPercentValue),
    deposit_amount_text: depositPercentText ? `${depositPercentText} процентов` : 'Размер не указан',
    deposit_percent: depositPercentText || '10',
    deposit_deadline: auction?.deposit_deadline || 'Срок не указан',
    deposit_bank_details: organizer?.bank_details || 'Реквизиты не указаны',
    
    // Сроки и периоды
    payment_period: '30',
    transfer_period: '10',
    
    // Банковские реквизиты и контакты
    bank_details: organizer?.bank_details || 'Реквизиты не указаны',
    buyer_details: 'Реквизиты покупателя (будут указаны после торгов)',
    seller_details: organizer?.details || organizer?.bank_details || 'Реквизиты продавца',
    participant_details: 'Реквизиты участника (будут указаны)',
    deposit_account_details: depositAccountDetails,
    payment_account_details: paymentAccountDetails,
    deposit_account_owner_name: depositAccountOwnerName,
    payment_account_owner_name: paymentAccountOwnerName,
    deposit_account_owner_inn: depositAccountOwnerInn,
    payment_account_owner_inn: paymentAccountOwnerInn,
    deposit_account_owner_type_code: depositAccountOwnerTypeCode,
    payment_account_owner_type_code: paymentAccountOwnerTypeCode,
    deposit_account_owner_type_label: depositAccountOwnerTypeLabel,
    payment_account_owner_type_label: paymentAccountOwnerTypeLabel,
    
    // Дополнительная информация
    documentation_address: organizer?.address || 'Адрес не указан',
    working_hours: 'Пн-Пт 9:00-18:00',
    contact_person: organizer?.executive_name || organizerFullName || 'Контактное лицо не указано',
    inspection_phone: inspectionPhone,
    inspection_email: inspectionEmail,
    contact_phone: organizer?.phone || '',
    etp_name: etp?.name || auction?.etp_name || '',
    etp_site: etp?.site || '',
    etp_operator: etp?.operator || ''
  };

  // Склонения для арбитражных судов (аналогично ФИО)
  const arbitrationCourtName = arbitrationCourtFull || 'Арбитражный суд не указан';
  const caseActCourtName = caseActCourtFull || arbitrationCourtName;
  const managerActCourtName = managerActCourtFull || caseActCourtName;
  Object.assign(
    variablesData,
    generateCourtDeclensionVariables('debtor_arbitration_court', arbitrationCourtName)
  );
  Object.assign(
    variablesData,
    generateCourtDeclensionVariables('debtor_case_act_court', caseActCourtName)
  );
  Object.assign(
    variablesData,
    generateCourtDeclensionVariables('debtor_manager_act_court', managerActCourtName)
  );

  Object.assign(
    variablesData,
    generateSimpleTextDeclensionVariables('debtor_manager_role', debtorManagerRoleCode || '')
  );

  const normalizeAuctionDateTime = (value) => {
    if (!value || typeof value !== 'string') return value;
    const raw = value.trim();
    const match = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/
    );
    if (!match) return value;
    const [, y, m, d, hh = '00', mm = '00', ss = '00'] = match;
    if (match[4]) {
      return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
    }
    return `${y}-${m}-${d}`;
  };

  const toTimestamp = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parsedLocal = parseLocalDateTime(value);
    if (parsedLocal instanceof Date && !Number.isNaN(parsedLocal.getTime())) {
      return parsedLocal.getTime();
    }
    const parsedNative = new Date(value);
    if (!Number.isNaN(parsedNative.getTime())) {
      return parsedNative.getTime();
    }
    return null;
  };

  const getPublicOfferFirstIntervalStart = (lotsData) => {
    if (!Array.isArray(lotsData) || lotsData.length === 0) return null;

    let earliest = null;
    let earliestTs = null;

    lotsData.forEach((lot) => {
      const periods = Array.isArray(lot?.public_offer_periods) ? lot.public_offer_periods : [];
      periods.forEach((row) => {
        const raw = row?.interval_start_datetime;
        const normalized = normalizeAuctionDateTime(raw || null);
        if (!normalized) return;
        const ts = toTimestamp(normalized);
        if (ts === null) return;
        if (earliestTs === null || ts < earliestTs) {
          earliestTs = ts;
          earliest = normalized;
        }
      });
    });

    return earliest;
  };

  const rawDateValues = {
    contract_date: new Date(),
    current_date: new Date(),
    agreement_date: new Date(),
    debtor_case_act_date: debtor?.case_act_date || null,
    debtor_manager_act_date: managerActDateValue
  };

  const auctionStartDatetimeValue = auction?.auction_type === 'public_offer'
    ? (
      getPublicOfferFirstIntervalStart(lots)
      || normalizeAuctionDateTime(auction?.applications_start || null)
    )
    : normalizeAuctionDateTime(auction?.auction_date || auction?.applications_start || null);

  const applicationDeadlineValue = normalizeAuctionDateTime(auction?.applications_end || null);

  const rawDateTimeValues = {
    auction_date: normalizeAuctionDateTime(auction?.auction_date || null),
    auction_start_datetime: auctionStartDatetimeValue,
    application_start_datetime: normalizeAuctionDateTime(auction?.applications_start || null),
    results_datetime: normalizeAuctionDateTime(auction?.results_datetime || null),
    application_deadline: applicationDeadlineValue
  };

  Object.entries(rawDateValues).forEach(([key, value]) => {
    variablesData[key] = formatDateByOption(value, 'default');
    DATE_FORMAT_OPTIONS.forEach(option => {
      if (!option.suffix) return;
      variablesData[`${key}${option.suffix}`] = formatDateByOption(value, option.id);
    });
  });

  Object.entries(rawDateTimeValues).forEach(([key, value]) => {
    variablesData[key] = formatDateTimeByOption(value, 'default');
    DATETIME_FORMAT_OPTIONS.forEach(option => {
      if (!option.suffix) return;
      variablesData[`${key}${option.suffix}`] = formatDateTimeByOption(value, option.id);
    });
  });

  if (variablesData.application_deadline_time_short === undefined) {
    variablesData.application_deadline_time_short = formatDateTimeByOption(applicationDeadlineValue, 'time_short');
  }

  return variablesData;
};

export default getDocumentVariablesData;
